/**
 * Formulario de edición de datos del merchant.
 * Solo permite editar campos según matriz de editabilidad HU-26-FE.
 *
 * @taskId T468
 * @see functional/Specs/specs_cycle_4.md HU-26-FE §Pantalla 4, §Matriz editabilidad
 */

import { useCallback, useState } from 'react';
import { X, AlertCircle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label } from '@flexibilitypay/ui';

import { useUpdateMerchant } from '../hooks/use-merchants';
import type { Merchant, MerchantEditPayload, MerchantStatus } from '../types/merchant.types';

// ─── Validaciones ───────────────────────────────────────────────────────────

interface ValidationErrors {
  email?: string;
  telefono?: string;
  domicilio?: string;
  codigoPostal?: string;
  motivo?: string;
}

function validateEmail(email: string): string | undefined {
  if (!email) return undefined;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Formato de email inválido';
  }
  return undefined;
}

function validateTelefono(telefono: string): string | undefined {
  if (!telefono) return undefined;
  const cleaned = telefono.replace(/\D/g, '');
  if (cleaned.length < 8) {
    return 'El teléfono debe tener al menos 8 dígitos';
  }
  return undefined;
}

function validateDomicilio(domicilio: string): string | undefined {
  if (!domicilio) return undefined;
  if (domicilio.length > 200) {
    return 'El domicilio no puede exceder 200 caracteres';
  }
  return undefined;
}

function validateCodigoPostal(cp: string): string | undefined {
  if (!cp) return undefined;
  if (!/^\d{4}$/.test(cp)) {
    return 'El código postal debe ser de 4 dígitos';
  }
  return undefined;
}

function validateMotivo(motivo: string, estadoCambiado: boolean): string | undefined {
  if (estadoCambiado && (!motivo || motivo.trim().length < 5)) {
    return 'El motivo es obligatorio y debe tener al menos 5 caracteres';
  }
  return undefined;
}

// ─── Props ──────────────────────────────────────────────────────────────────

export interface MerchantEditFormProps {
  merchant: Merchant;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Si el usuario tiene rol PSP_ADMIN */
  canChangeStatus?: boolean;
}

// ─── Componente principal ───────────────────────────────────────────────────

export function MerchantEditForm({
  merchant,
  open,
  onClose,
  onSuccess,
  canChangeStatus = false,
}: MerchantEditFormProps) {
  const { t } = useTranslation(['merchants', 'common']);

  const [formData, setFormData] = useState({
    email: merchant.email,
    telefono: merchant.telefono,
    domicilio: merchant.domicilio,
    codigoPostal: merchant.codigoPostal,
    estado: merchant.estado,
    motivo: '',
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const updateMutation = useUpdateMerchant();

  // Reset form cuando cambia el merchant (derived state, ver React docs:
  // "Adjusting some state when a prop changes")
  const [prevMerchant, setPrevMerchant] = useState(merchant);
  if (merchant !== prevMerchant) {
    setPrevMerchant(merchant);
    setFormData({
      email: merchant.email,
      telefono: merchant.telefono,
      domicilio: merchant.domicilio,
      codigoPostal: merchant.codigoPostal,
      estado: merchant.estado,
      motivo: '',
    });
    setErrors({});
    setTouched({});
  }

  const estadoCambiado = formData.estado !== merchant.estado;

  // Validar campo individual
  const validateField = useCallback(
    (field: keyof typeof formData, value: string) => {
      switch (field) {
        case 'email':
          return validateEmail(value);
        case 'telefono':
          return validateTelefono(value);
        case 'domicilio':
          return validateDomicilio(value);
        case 'codigoPostal':
          return validateCodigoPostal(value);
        case 'motivo':
          return validateMotivo(value, estadoCambiado);
        default:
          return undefined;
      }
    },
    [estadoCambiado],
  );

  // Validar todo el formulario
  const validateForm = useCallback(() => {
    const newErrors: ValidationErrors = {
      email: validateEmail(formData.email),
      telefono: validateTelefono(formData.telefono),
      domicilio: validateDomicilio(formData.domicilio),
      codigoPostal: validateCodigoPostal(formData.codigoPostal),
      motivo: validateMotivo(formData.motivo, estadoCambiado),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  }, [formData, estadoCambiado]);

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleBlur = (field: keyof typeof formData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload: MerchantEditPayload = {};

    // Solo incluir campos que cambiaron
    if (formData.email !== merchant.email) {
      payload.email = formData.email;
    }
    if (formData.telefono !== merchant.telefono) {
      payload.telefono = formData.telefono;
    }
    if (formData.domicilio !== merchant.domicilio) {
      payload.domicilio = formData.domicilio;
    }
    if (formData.codigoPostal !== merchant.codigoPostal) {
      payload.codigoPostal = formData.codigoPostal;
    }
    if (estadoCambiado) {
      payload.estado = formData.estado;
      payload.motivo = formData.motivo;
    }

    // No enviar si no hay cambios
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    updateMutation.mutate(
      { id: merchant.id, payload },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      },
    );
  };

  const hasChanges =
    formData.email !== merchant.email ||
    formData.telefono !== merchant.telefono ||
    formData.domicilio !== merchant.domicilio ||
    formData.codigoPostal !== merchant.codigoPostal ||
    estadoCambiado;

  const handleClose = () => {
    if (hasChanges) {
      // TODO: Mostrar modal de confirmación
      const confirmed = window.confirm(
        t('merchants:editForm.discardChanges', '¿Descartar los cambios sin guardar?'),
      );
      if (!confirmed) return;
    }
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-black/50" aria-hidden="true" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-lg bg-background shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 className="text-lg font-semibold">
              {t('merchants:editForm.title', 'Editar Merchant')}
            </h2>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              {/* Campos inmutables (solo lectura) */}
              <div className="rounded-lg bg-muted/50 p-4">
                <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                  <Info className="h-4 w-4" />
                  {t('merchants:editForm.readonlyHint', 'Campos no editables')}
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Razón Social</span>
                    <p className="font-medium">{merchant.razonSocial}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">CUIT</span>
                    <p className="font-mono">{merchant.cuit}</p>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="edit-email">
                  {t('merchants:editForm.email', 'Email de Contacto')}
                </Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && (
                  <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                    <AlertCircle className="h-3 w-3" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Teléfono */}
              <div>
                <Label htmlFor="edit-telefono">
                  {t('merchants:editForm.telefono', 'Teléfono')}
                </Label>
                <Input
                  id="edit-telefono"
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => handleChange('telefono', e.target.value)}
                  onBlur={() => handleBlur('telefono')}
                  className={errors.telefono ? 'border-destructive' : ''}
                />
                {errors.telefono && (
                  <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                    <AlertCircle className="h-3 w-3" />
                    {errors.telefono}
                  </p>
                )}
              </div>

              {/* Domicilio */}
              <div>
                <Label htmlFor="edit-domicilio">
                  {t('merchants:editForm.domicilio', 'Domicilio')}
                </Label>
                <Input
                  id="edit-domicilio"
                  type="text"
                  value={formData.domicilio}
                  onChange={(e) => handleChange('domicilio', e.target.value)}
                  onBlur={() => handleBlur('domicilio')}
                  maxLength={200}
                  className={errors.domicilio ? 'border-destructive' : ''}
                />
                {errors.domicilio && (
                  <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                    <AlertCircle className="h-3 w-3" />
                    {errors.domicilio}
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  {formData.domicilio.length}/200
                </p>
              </div>

              {/* Código Postal */}
              <div>
                <Label htmlFor="edit-cp">
                  {t('merchants:editForm.codigoPostal', 'Código Postal')}
                </Label>
                <Input
                  id="edit-cp"
                  type="text"
                  value={formData.codigoPostal}
                  onChange={(e) => handleChange('codigoPostal', e.target.value)}
                  onBlur={() => handleBlur('codigoPostal')}
                  maxLength={4}
                  className={`w-24 ${errors.codigoPostal ? 'border-destructive' : ''}`}
                />
                {errors.codigoPostal && (
                  <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                    <AlertCircle className="h-3 w-3" />
                    {errors.codigoPostal}
                  </p>
                )}
              </div>

              {/* Estado (solo PSP_ADMIN) */}
              {canChangeStatus && (
                <>
                  <div>
                    <Label htmlFor="edit-estado">
                      {t('merchants:editForm.estado', 'Estado')}
                      <span className="ml-2 text-xs text-muted-foreground">(PSP_ADMIN)</span>
                    </Label>
                    <select
                      id="edit-estado"
                      value={formData.estado}
                      onChange={(e) => handleChange('estado', e.target.value as MerchantStatus)}
                      className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="ACTIVE">Activo</option>
                      <option value="INACTIVE">Inactivo</option>
                      <option value="CLOSED">Baja</option>
                    </select>
                  </div>

                  {/* Motivo (obligatorio si cambia estado) */}
                  {estadoCambiado && (
                    <div>
                      <Label htmlFor="edit-motivo">
                        {t('merchants:editForm.motivo', 'Motivo del cambio')}
                        <span className="text-destructive">*</span>
                      </Label>
                      <textarea
                        id="edit-motivo"
                        value={formData.motivo}
                        onChange={(e) => handleChange('motivo', e.target.value)}
                        onBlur={() => handleBlur('motivo')}
                        rows={3}
                        className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm ${
                          errors.motivo ? 'border-destructive' : 'border-input'
                        } bg-background`}
                        placeholder={t(
                          'merchants:editForm.motivoPlaceholder',
                          'Ingrese el motivo del cambio de estado...',
                        )}
                      />
                      {errors.motivo && (
                        <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                          <AlertCircle className="h-3 w-3" />
                          {errors.motivo}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Metadato de auditoría */}
              <p className="text-xs text-muted-foreground">
                {t('merchants:editForm.lastUpdate', 'Última actualización por')}{' '}
                <span className="font-medium">{merchant.updatedBy}</span>{' '}
                {t('merchants:editForm.on', 'el')}{' '}
                {new Date(merchant.updatedAt).toLocaleString('es-AR')}
              </p>
            </div>

            {/* Error de API */}
            {updateMutation.error && (
              <div className="mt-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="mr-2 inline h-4 w-4" />
                {updateMutation.error.code === 'CAMPO_NO_EDITABLE'
                  ? t('merchants:errors.campoNoEditable', 'Uno de los campos no es editable')
                  : t('merchants:errors.updateFailed', 'Error al guardar los cambios')}
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleClose}>
                {t('common:cancel', 'Cancelar')}
              </Button>
              <Button
                type="submit"
                disabled={!hasChanges || updateMutation.isPending}
              >
                {updateMutation.isPending
                  ? t('common:saving', 'Guardando...')
                  : t('common:save', 'Guardar Cambios')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
